import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;
  #browserColors = null;
  #applyBrowserColor = () => {
    if (!isMobileBreakpoint() || this.#browserColors) return;
    const metas = [...document.head.querySelectorAll('meta[name="theme-color"]')];
    const created = metas.length === 0 ? document.createElement('meta') : null;
    if (created) { created.name = 'theme-color'; document.head.prepend(created); metas.push(created); }
    this.#browserColors = {
      metas: metas.map(meta => [meta, meta.getAttribute('content')]),
      created,
      background: document.documentElement.style.getPropertyValue('background-color'),
      priority: document.documentElement.style.getPropertyPriority('background-color')
    };
    metas.forEach(meta => meta.setAttribute('content', '#4b182b'));
    document.documentElement.style.setProperty('background-color', '#4b182b', 'important');
  };
  #restoreBrowserColor = () => {
    if (!this.#browserColors) return;
    const { metas, created, background, priority } = this.#browserColors;
    metas.forEach(([meta, content]) => content === null ? meta.removeAttribute('content') : meta.setAttribute('content', content));
    created?.remove();
    if (background) document.documentElement.style.setProperty('background-color', background, priority);
    else document.documentElement.style.removeProperty('background-color');
    this.#browserColors = null;
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    this.#restoreBrowserColor();
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    this.#applyBrowserColor();
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#restoreBrowserColor();
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      const { cartDrawerOpen, ...remainingState } = history.state;
      history.replaceState(remainingState, '');
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (location.pathname.includes('/products/')) {
      try { sessionStorage.setItem('bluvvy-last-added-product', location.pathname + location.search); } catch {}
    }
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #opening = false;

  showDialog() {
    if (this.#opening || this.refs.dialog?.open) return;
    this.#opening = true;
    super.showDialog();
    requestAnimationFrame(() => { this.#opening = false; });
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  async renderContents(cart, open = false) {
    const items = this.querySelector('cart-items-component');
    if (!items || !cart || cart.status) return;
    const sectionId = items.dataset.sectionId;
    if (!sectionId) return;
    const wasOpen = this.refs.dialog?.open;
    try {
      let sections = cart.sections;
      if (!sections?.[sectionId]) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Cart section could not be refreshed');
        sections = { [sectionId]: await response.text() };
      }
      if (typeof sections?.[sectionId] !== 'string') throw new Error('Cart section is missing');
      if (cart.item_count === 0) this.close();
      await morphSection(sectionId, sections[sectionId], { mode: 'hydration' });
      const currentItems = this.querySelector('cart-items-component');
      currentItems?.dispatchEvent(new CartUpdateEvent(cart, sectionId, {
        itemCount: cart.item_count,
        source: 'cart-items-component',
        sections,
      }));
      if (cart.item_count > 0 && (wasOpen || open)) this.showDialog();
      this.#updateStickyState();
    } catch (error) {
      console.error(error);
      if (this.refs.liveRegion) this.refs.liveRegion.textContent = 'Your cart was updated. Please reopen your bag to refresh it.';
    }
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

